/**
 * Seating rules kept on the guests: "keep together" groups and "keep apart" pairs. Pure; shared by the planner
 * screen, the warnings and automatic seating.
 */
import type { Guest } from "./types";
import { canonTable } from "./tableName";

const squash = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
const live = (guests: readonly Guest[]) => guests.filter((g) => g.status !== "declined");

/** Names in an "apart" cell or a rule line: separated by semicolons, pipes or plus signs. */
export const splitNames = (s: string | undefined): string[] => (s ?? "").split(/[;|+]/).map((x) => x.trim()).filter(Boolean);

/** The guest a name means, or undefined when no one (or more than one person) has that name. */
export function guestByName(guests: readonly Guest[], name: string): Guest | undefined {
  const k = squash(name);
  const hit = guests.filter((g) => squash(g.name) === k);
  return hit.length === 1 ? hit[0] : undefined;
}

/** Groups of two or more coming guests with the same together label, keyed by the label. */
export function togetherGroups(guests: readonly Guest[]): Map<string, Guest[]> {
  const out = new Map<string, Guest[]>();
  for (const g of live(guests)) {
    const k = squash(g.together ?? "");
    if (k) out.set(k, [...(out.get(k) ?? []), g]);
  }
  Array.from(out.keys()).forEach((k) => { if ((out.get(k) as Guest[]).length < 2) out.delete(k); });
  return out;
}

/** Ids this guest should not share a table with. A rule written on either guest counts for both. */
export function apartIds(guests: readonly Guest[]): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  const link = (a: string, b: string) => { if (a === b) return; (out.get(a) ?? out.set(a, new Set()).get(a)!).add(b); (out.get(b) ?? out.set(b, new Set()).get(b)!).add(a); };
  for (const g of live(guests)) for (const n of splitNames(g.apart)) { const o = guestByName(live(guests), n); if (o) link(g.id, o.id); }
  return out;
}

/** Warnings when a rule is broken by where people sit now (after manual moves, an import or a rule change). */
export function ruleBreaks(guests: readonly Guest[]): string[] {
  const out: string[] = [];
  const tableOf = (g: Guest) => canonTable(g.table).toLowerCase();
  const label = (g: Guest) => canonTable(g.table);
  for (const grp of Array.from(togetherGroups(guests).values())) {
    const seated = grp.filter((g) => tableOf(g));
    const tables = Array.from(new Set(seated.map(tableOf)));
    if (tables.length > 1) out.push(`${grp.map((g) => g.name).join(" + ")} should sit together but are at ${Array.from(new Set(seated.map(label))).join(", ")}.`);
    else if (seated.length && seated.length < grp.length) out.push(`${grp.map((g) => g.name).join(" + ")} should sit together but ${grp.length - seated.length} of them ${grp.length - seated.length === 1 ? "has" : "have"} no table yet.`);
  }
  const byId = new Map(live(guests).map((g) => [g.id, g]));
  const done = new Set<string>();
  apartIds(guests).forEach((others, id) => others.forEach((o) => {
    const key = [id, o].sort().join("|");
    if (done.has(key)) return;
    done.add(key);
    const a = byId.get(id), b = byId.get(o);
    if (a && b && tableOf(a) && tableOf(a) === tableOf(b)) out.push(`${a.name} and ${b.name} should be kept apart but both sit at ${label(a)}.`);
  }));
  return out;
}

export interface RuleLines { together: string[]; apart: string[] }

/** The rules as lines a person can read and edit: "Ann + Bob" for a group, "Dan + Eve" for a pair to keep apart. */
export function rulesToLines(guests: readonly Guest[]): RuleLines {
  const together: string[] = [];
  Array.from(togetherGroups(guests).entries()).forEach(([k, grp]) => {
    const names = grp.map((g) => g.name).join(" + ");
    const label = (grp[0].together ?? "").trim();
    together.push(squash(label) === squash(names) ? names : `${label}: ${names}`);
  });
  const apart: string[] = [];
  const byId = new Map(live(guests).map((g) => [g.id, g]));
  const done = new Set<string>();
  apartIds(guests).forEach((others, id) => others.forEach((o) => {
    const key = [id, o].sort().join("|");
    if (done.has(key)) return;
    done.add(key);
    const a = byId.get(id), b = byId.get(o);
    if (a && b) apart.push(`${a.name} + ${b.name}`);
  }));
  return { together, apart };
}

/**
 * Replaces every rule with the ones in the text. One group per line in "together" ("Ann + Bob + Cy", or
 * "Smith family: Ann + Bob" to name it); one pair or set per line in "apart" ("Dan + Eve"). Names must match a guest.
 */
export function applyRuleLines(guests: readonly Guest[], togetherText: string, apartText: string): { guests: Guest[]; problems: string[] } {
  const problems: string[] = [];
  // Rules of declined guests are not shown or edited here, so they are kept as they are.
  const next: Guest[] = guests.map((g) => { if (g.status === "declined") return g; const { together: _t, apart: _a, ...rest } = g; return rest as Guest; });
  const find = (n: string) => { const g = guestByName(next, n); if (!g) problems.push(`No single guest is named "${n}".`); return g; };
  const index = new Map(next.map((g, i) => [g.id, i]));
  const lines = (t: string) => t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  for (const line of lines(togetherText)) {
    const m = /^([^:+]+):\s*(.+)$/.exec(line);
    const names = splitNames(m ? m[2] : line);
    const members = names.map(find).filter((x): x is Guest => !!x);
    if (names.length < 2) { problems.push(`"${line}" needs at least two names joined with +.`); continue; }
    if (members.length < 2) continue;
    const label = (m ? m[1].trim() : members.map((x) => x.name).join(" + ")).slice(0, 300);
    for (const x of members) next[index.get(x.id)!] = { ...next[index.get(x.id)!], together: label };
  }
  for (const line of lines(apartText)) {
    const names = splitNames(line);
    const members = names.map(find).filter((x): x is Guest => !!x);
    if (names.length < 2) { problems.push(`"${line}" needs at least two names joined with +.`); continue; }
    for (const x of members) {
      const i = index.get(x.id)!;
      const others = members.filter((y) => y.id !== x.id).map((y) => y.name);
      const have = splitNames(next[i].apart);
      const all = Array.from(new Set([...have, ...others]));
      next[i] = { ...next[i], apart: all.join("; ").slice(0, 300) };
    }
  }
  return { guests: next, problems };
}
